<script>
    import { onMount } from 'svelte';
    import { nanoid } from 'nanoid'

	import lookups from "../stores/lookupStore";

    import FormRow from './FormRow.svelte';
    import FormColumn from './FormColumn.svelte';
    import Select from './Select.svelte';
    import Input from './Input.svelte';
    import Country from './Country.svelte';
    import { validator } from '../validation';

    export let idPrefix = nanoid(8);
    export let googlePlaceJson = null;
    export let formattedAddress = null;
    export let subpremise = null;
    export let premise = null;
    export let street = null;
    export let suburb = null;
    export let state = null;
    export let postcode = null;
    export let country = null;
    export let countryCode = null;
    export let lat = null;
    export let lng = null;
    export let placeId = null;
    export let url = null;
    export let disabled;
    export let required;

    let addressInput;
    let autocomplete;
    let uniqueId = 'i' + nanoid(8);

    const updateAddressLine = (subpremise, premise, street) => {
        let addressLine
        if (subpremise) {
            addressLine = subpremise;
        }
        if (premise) {
            addressLine = (addressLine) ? `${addressLine}, ${premise}` : premise;
        }
        if (street) {
            addressLine = (addressLine) ? `${addressLine}, ${street}` : street;
        }
        if (addressLine && addressInput) addressInput.value = addressLine;
    }

    $: {
        updateAddressLine(subpremise, premise, street);
    }

    $: {
        formattedAddress = '';
        if (subpremise) {
            formattedAddress = subpremise;
        }
        if (premise) {
            formattedAddress = (formattedAddress) ? `${formattedAddress}, ${premise}` : premise;
        }
        if (street) {
            formattedAddress = (formattedAddress) ? `${formattedAddress}, ${street}` : street;
        }
        if (suburb) {
            formattedAddress = (formattedAddress) ? `${formattedAddress}, ${suburb}` : suburb;
        }
        if (state) {
            formattedAddress = (formattedAddress) ? `${formattedAddress} ${state}` : state;
        }
        if (postcode) {
            formattedAddress = (formattedAddress) ? `${formattedAddress} ${postcode}` : postcode;
        }
        if (country) {
            formattedAddress = (formattedAddress) ? `${formattedAddress}, ${country}` : country;
        }
        if (!street) {
            formattedAddress = null;
            if (addressInput) addressInput.value = '';
        }
    }

    let googleError = false;

    function initiaise() {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(function (position) {
                var geolocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                };
                var circle = new google.maps.Circle(
                    { center: geolocation, radius: position.coords.accuracy });
                autocomplete.setBounds(circle.getBounds());
            });
        }
    }

    function clearErrorDisplay() {
        let section = document.querySelector(`#${uniqueId}`)
        section && $validator.clearAllVisualError(section);
    }

    onMount(() => {

        updateAddressLine(subpremise, premise, street);

        if (!disabled) {
            try {
                const defaultBounds = new google.maps.LatLngBounds(
                    new google.maps.LatLng(-37.812474, 144.956944),
                    new google.maps.LatLng(-37.812474, 144.956944));

                const options = {
                    bounds: defaultBounds,
                    types: ['geocode']
                };

                //  In case there is no selection made
                addressInput.addEventListener("change", (e) => {
                    street = e.target.value;
                });

                autocomplete = new google.maps.places.Autocomplete(addressInput, options);

                google.maps.event.addListener(autocomplete, 'place_changed', function () {
                    var result = autocomplete.getPlace();

                    if (result) {
                        var components = {};
                        var shortComponents = {};
                        result.address_components.forEach((item, i) => {
                            components[item.types[0]] = item.long_name;
                            shortComponents[item.types[0]] = item.short_name;
                        });
                        googlePlaceJson = JSON.stringify(components);

                        subpremise = components["subpremise"] || null;
                        premise = components["premise"] || null;
                        street = (components["street_number"] ? components["street_number"] + " " : "") + (components["route"] ? components["route"] : "");
                        suburb = components["locality"] || "";
                        state = components["administrative_area_level_1"] || "";
                        postcode = components["postal_code"] || "";
                        country = components["country"];
                        countryCode = shortComponents["country"];

                        if (countryCode === "AU") {

                            if ($lookups.AustralianStates.find(s => s.label == state)) {
                                state = $lookups.AustralianStates.find(s => s.label == state).value;
                            }

                        }
                        formattedAddress = result.formatted_address;

                        if (result.geometry) {
                            lat = result.geometry.location.lat();
                            lng = result.geometry.location.lng();
                        }
                        placeId = result.place_id;
                        url = result.url;

                        $validator.validate(addressInput);
                        clearErrorDisplay();

                        result = {};
                    }
                });

            } catch (error) {
                console.error("Mapping Error:", error);
                googleError = true;
            }
        }

    });

</script>

<section id="{uniqueId}">

    <FormRow>
        <FormColumn cols="8">
            <label for="{idPrefix}_address" class:isrequired="{required}">Address</label>
            {#if googleError }
            <Input {required} {disabled} maxlength="50" street bind:value="{street}" id="{idPrefix}_street" />
            {:else}
            <input {disabled} maxlength="50" street type="text" bind:this="{addressInput}" class="form-control" on:focus="{initiaise}" {required} id="{idPrefix}_address" />
            {/if}
        </FormColumn>
        <FormColumn cols="4">
            <Input {required} {disabled} maxlength="50" label="Suburb" bind:value="{suburb}" id="{idPrefix}_suburb" />
        </FormColumn>
    </FormRow>

    <FormRow>
        <FormColumn cols="4">
            {#if countryCode == "AU"}
			    <Select {required} {disabled}
				    id="{idPrefix}_State"
				    label="State"
				    optionList={$lookups.AustralianStates}
				    bind:value={state}
			    />
            {:else}
                <Input {required} {disabled} maxlength="50" label="State" bind:value="{state}" id="{idPrefix}_state" />
            {/if}
        </FormColumn>
        <FormColumn cols="4">
            <Input {required} {disabled} maxlength="{countryCode == 'AU' ? 4 : 15}" label="Postcode" bind:value="{postcode}" id="{idPrefix}_postcode" />
        </FormColumn>
        <FormColumn cols="4">
            <Country  {required} {disabled} label="Country" bind:value="{countryCode}"id="{idPrefix}_country"></Country>
        </FormColumn>
    </FormRow>

</section>